import type { ActivityHostActionCreators } from "@/features/activity-host";
import type {
  InstructionPayload,
  InstructionSessionState
} from "@/game/templates/instruction";
import type { ScenarioPayload } from "@/game/templates/scenario";
import type {
  TargetSearchPayload,
  TargetSearchState
} from "@/game/templates/search";
import { getFocusSocialB4AssetManifest } from "@/assets/requirements/focus-social/b4-focus-social-manifests";

import { FocusSocialFollowDirectionsRenderer } from "./FocusSocialFollowDirectionsRenderer";
import { FocusSocialFriendshipStoryRenderer } from "./FocusSocialFriendshipStoryRenderer";
import { FocusSocialTargetSearchRenderer } from "./FocusSocialTargetSearchRenderer";
import {
  focusSocialFollowDirectionsAdapter,
  type FocusSocialFollowDirectionsAction,
  type FocusSocialFollowDirectionsRenderModel
} from "./follow-directions-adapter";
import {
  focusSocialFriendshipStoryAdapter,
  type FocusSocialFriendshipStoryAction,
  type FocusSocialFriendshipStoryRenderModel,
  type FocusSocialFriendshipStoryState
} from "./friendship-story-adapter";
import {
  focusSocialFollowDirectionsPlayerLevels,
  focusSocialFriendshipStoryPlayerLevels,
  focusSocialTargetSearchPlayerLevels
} from "./levels";
import {
  focusSocialTargetSearchAdapter,
  type FocusSocialTargetSearchAction,
  type FocusSocialTargetSearchRenderModel
} from "./target-search-adapter";
import type { FocusSocialPlayerBinding } from "./types";

export type FocusSocialTargetSearchPlayerBinding =
  FocusSocialPlayerBinding<
    "target-search",
    TargetSearchPayload,
    TargetSearchState,
    FocusSocialTargetSearchAction,
    FocusSocialTargetSearchRenderModel
  >;

export type FocusSocialFollowDirectionsPlayerBinding =
  FocusSocialPlayerBinding<
    "follow-directions",
    InstructionPayload,
    InstructionSessionState,
    FocusSocialFollowDirectionsAction,
    FocusSocialFollowDirectionsRenderModel
  >;

export type FocusSocialFriendshipStoryPlayerBinding =
  FocusSocialPlayerBinding<
    "friendship-stories",
    ScenarioPayload,
    FocusSocialFriendshipStoryState,
    FocusSocialFriendshipStoryAction,
    FocusSocialFriendshipStoryRenderModel
  >;

export type FocusSocialAnyPlayerBinding =
  | FocusSocialTargetSearchPlayerBinding
  | FocusSocialFollowDirectionsPlayerBinding
  | FocusSocialFriendshipStoryPlayerBinding;

export const focusSocialTargetSearchActions = {
  requestHint: () => ({ type: "request-hint" }),
  retry: () => ({ type: "clear-hint" }),
  replay: () => ({ type: "replay" })
} satisfies ActivityHostActionCreators<FocusSocialTargetSearchAction>;

export const focusSocialFollowDirectionsActions = {
  requestHint: () => ({ type: "request-hint" }),
  replay: () => ({ type: "restart" })
} satisfies ActivityHostActionCreators<FocusSocialFollowDirectionsAction>;

export const focusSocialFriendshipStoryActions = {
  requestHint: () => ({ type: "request-hint" }),
  replay: () => ({ type: "reset" })
} satisfies ActivityHostActionCreators<FocusSocialFriendshipStoryAction>;

export const focusSocialTargetSearchBindings:
  readonly FocusSocialTargetSearchPlayerBinding[] =
  focusSocialTargetSearchPlayerLevels.map((level) => ({
    domain: "focus-social",
    activityType: "target-search",
    levelId: level.id,
    level,
    adapter: focusSocialTargetSearchAdapter,
    actions: focusSocialTargetSearchActions,
    assets: getFocusSocialB4AssetManifest(level.id),
    renderActivity: FocusSocialTargetSearchRenderer
  }));

export const focusSocialFollowDirectionsBindings:
  readonly FocusSocialFollowDirectionsPlayerBinding[] =
  focusSocialFollowDirectionsPlayerLevels.map((level) => ({
    domain: "focus-social",
    activityType: "follow-directions",
    levelId: level.id,
    level,
    adapter: focusSocialFollowDirectionsAdapter,
    actions: focusSocialFollowDirectionsActions,
    assets: getFocusSocialB4AssetManifest(level.id),
    renderActivity: FocusSocialFollowDirectionsRenderer
  }));

export const focusSocialFriendshipStoryBindings:
  readonly FocusSocialFriendshipStoryPlayerBinding[] =
  focusSocialFriendshipStoryPlayerLevels.map((level) => ({
    domain: "focus-social",
    activityType: "friendship-stories",
    levelId: level.id,
    level,
    adapter: focusSocialFriendshipStoryAdapter,
    actions: focusSocialFriendshipStoryActions,
    assets: getFocusSocialB4AssetManifest(level.id),
    renderActivity: FocusSocialFriendshipStoryRenderer
  }));

export const focusSocialPlayerBindings:
  readonly FocusSocialAnyPlayerBinding[] = [
    ...focusSocialTargetSearchBindings,
    ...focusSocialFollowDirectionsBindings,
    ...focusSocialFriendshipStoryBindings
  ];

export function getFocusSocialPlayerBinding(
  levelId: string
): FocusSocialAnyPlayerBinding | undefined {
  return focusSocialPlayerBindings.find(
    (binding) => binding.levelId === levelId
  );
}
